import React, { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Tooltip from '@material-ui/core/Tooltip';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import PeopleIcon from '@material-ui/icons/People';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { getUsers } from '../../services/userService';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getBackendUrl } from '../../config';

const backendUrl = getBackendUrl();

const PANEL_WIDTH = 64;

const useStyles = makeStyles((theme) => ({
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: PANEL_WIDTH,
    backgroundColor: '#ffffff',
    borderLeft: '1px solid #e5e7eb',
    boxShadow: '-4px 0 16px rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    zIndex: 1300,
    transition: 'transform 0.25s ease',
    paddingTop: 64,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  panelHidden: {
    transform: `translateX(${PANEL_WIDTH}px)`,
  },
  toggleTab: {
    position: 'fixed',
    top: '50%',
    right: 0,
    transform: 'translateY(-50%)',
    zIndex: 1301,
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRight: 'none',
    borderRadius: '8px 0 0 8px',
    boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
    padding: '8px 4px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    transition: 'right 0.25s ease',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  toggleTabOpen: {
    right: PANEL_WIDTH,
  },
  toggleIcon: {
    color: '#6b7280',
    fontSize: 18,
  },
  onlineCount: {
    fontSize: 10,
    fontWeight: 700,
    color: '#22c55e',
    lineHeight: 1,
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '12px 0 16px',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  avatarWrapper: {
    position: 'relative',
    flexShrink: 0,
  },
  avatar: {
    width: 36,
    height: 36,
    fontSize: 14,
    fontWeight: 600,
    backgroundColor: '#3b82f6',
    cursor: 'default',
  },
  statusDot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 11,
    height: 11,
    borderRadius: '50%',
    border: '2px solid #ffffff',
  },
  online: {
    backgroundColor: '#22c55e',
  },
  offline: {
    backgroundColor: '#d1d5db',
  },
  sectionLabel: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#9ca3af',
    marginBottom: 4,
  },
  divider: {
    width: 32,
    height: 1,
    backgroundColor: '#f3f4f6',
    margin: '4px 0',
  },
}));

const getAvatarSrc = (profileImage, companyId) => {
  if (!profileImage) return null;
  if (profileImage.startsWith('http')) return profileImage;
  return `${backendUrl}/public/company${companyId}/profile-images/${profileImage}`;
};

const getInitials = (name = '') =>
  name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

const UsersPresencePanel = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await getUsers({ limit: 100 });
      setUsers(data.users || []);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const hasSocket = socket && typeof socket.on === 'function';
    if (!hasSocket || !user?.companyId) return;

    const event = `company-${user.companyId}-user`;

    const handler = (data) => {
      if (data.action === 'update') {
        setUsers((prev) => prev.map((u) => (u.id === data.user.id ? { ...u, ...data.user } : u)));
        // Re-fetch para garantir consistência caso o evento chegue atrasado
        fetchUsers();
      } else if (data.action === 'create') {
        setUsers((prev) => [...prev, data.user]);
      } else if (data.action === 'delete') {
        setUsers((prev) => prev.filter((u) => u.id !== data.user.id));
      }
    };

    socket.on(event, handler);
    return () => socket.off(event, handler);
  }, [socket, user?.companyId, fetchUsers]);

  const online = users.filter((u) => u.online);
  const offline = users.filter((u) => !u.online);

  const UserAvatar = ({ u }) => (
    <Tooltip title={`${u.name}${u.online ? ' · Online' : ' · Offline'}`} placement="left" arrow>
      <Box className={classes.avatarWrapper}>
        <Avatar
          className={classes.avatar}
          src={getAvatarSrc(u.profileImage, u.companyId)}
          alt={u.name}
        >
          {getInitials(u.name)}
        </Avatar>
        <Box className={`${classes.statusDot} ${u.online ? classes.online : classes.offline}`} />
      </Box>
    </Tooltip>
  );

  return (
    <>
      {/* Aba de toggle */}
      <Box
        className={`${classes.toggleTab} ${open ? classes.toggleTabOpen : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        <PeopleIcon style={{ fontSize: 16, color: '#6b7280' }} />
        {online.length > 0 && (
          <Typography className={classes.onlineCount}>{online.length}</Typography>
        )}
        {open ? (
          <ChevronRightIcon className={classes.toggleIcon} />
        ) : (
          <ChevronLeftIcon className={classes.toggleIcon} />
        )}
      </Box>

      {/* Painel */}
      <Box className={`${classes.panel} ${open ? '' : classes.panelHidden}`}>
        <Box className={classes.scrollArea}>
          {online.length > 0 && (
            <>
              <Typography className={classes.sectionLabel}>Online</Typography>
              {online.map((u) => (
                <UserAvatar key={u.id} u={u} />
              ))}
            </>
          )}

          {online.length > 0 && offline.length > 0 && <Box className={classes.divider} />}

          {offline.length > 0 && (
            <>
              <Typography className={classes.sectionLabel}>Offline</Typography>
              {offline.map((u) => (
                <UserAvatar key={u.id} u={u} />
              ))}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default UsersPresencePanel;
